// Futures contracts for the calculators: dollars per 1.00 point and the minimum tick.
// Point values match the ones used to read Tradovate imports (csv.js).
export const CONTRACTS = [
  { symbol: 'MNQ', name: 'Micro E-mini Nasdaq-100', point: 2, tick: 0.25, full: 'NQ' },
  { symbol: 'NQ', name: 'E-mini Nasdaq-100', point: 20, tick: 0.25, micro: 'MNQ' },
  { symbol: 'MES', name: 'Micro E-mini S&P 500', point: 5, tick: 0.25, full: 'ES' },
  { symbol: 'NES', name: 'E-nano S&P 500', point: 0.5, tick: 0.5, full: 'ES' },
  { symbol: 'NNQ', name: 'E-nano Nasdaq-100', point: 0.2, tick: 0.5, full: 'NQ' },
  { symbol: 'N2K', name: 'E-nano Russell 2000', point: 0.5, tick: 0.2, full: 'RTY' },
  { symbol: 'NDOW', name: 'E-nano Dow', point: 0.05, tick: 2, full: 'YM' },
  { symbol: 'ES', name: 'E-mini S&P 500', point: 50, tick: 0.25, micro: 'MES' },
  { symbol: 'MYM', name: 'Micro E-mini Dow', point: 0.5, tick: 1, full: 'YM' },
  { symbol: 'YM', name: 'E-mini Dow', point: 5, tick: 1, micro: 'MYM' },
  { symbol: 'M2K', name: 'Micro E-mini Russell 2000', point: 5, tick: 0.1, full: 'RTY' },
  { symbol: 'RTY', name: 'E-mini Russell 2000', point: 50, tick: 0.1, micro: 'M2K' },
  { symbol: 'MCL', name: 'Micro Crude Oil', point: 100, tick: 0.01, full: 'CL' },
  { symbol: 'CL', name: 'Crude Oil', point: 1000, tick: 0.01, micro: 'MCL' },
  { symbol: 'MGC', name: 'Micro Gold', point: 10, tick: 0.1, full: 'GC' },
  { symbol: 'GC', name: 'Gold', point: 100, tick: 0.1, micro: 'MGC' },
  { symbol: 'SIL', name: 'Micro Silver (1,000 oz)', point: 1000, tick: 0.005, full: 'SI' },
  { symbol: 'SI', name: 'Silver', point: 5000, tick: 0.005, micro: 'SIL' },
  { symbol: 'M6E', name: 'Micro Euro FX', point: 12500, tick: 0.0001, full: '6E' },
  { symbol: '6E', name: 'Euro FX', point: 125000, tick: 0.00005, micro: 'M6E' },
  { symbol: 'ZN', name: '10-Year T-Note', point: 1000, tick: 0.015625 },
  { symbol: 'ZB', name: '30-Year T-Bond', point: 1000, tick: 0.03125 },
];

export function findContract(symbol) {
  return CONTRACTS.find((c) => c.symbol === symbol) || CONTRACTS[0];
}

// The contract a symbol belongs to, without its month code: "MNQZ5" and "/MNQ" are both MNQ.
export function rootSymbol(symbol) {
  const s = String(symbol || '').toUpperCase().trim().replace(/^\//, '');
  const m = s.match(/^([A-Z0-9]{1,4}?)([FGHJKMNQUVXZ]\d{1,2})?$/);
  return m ? m[1] : s;
}

// The known futures contract for a symbol (any month), or null.
export function contractFor(symbol) {
  const root = rootSymbol(symbol);
  return CONTRACTS.find((c) => c.symbol === root) || null;
}

// Rounds a distance in points to whole ticks.
export function toTicks(points, contract) {
  return Math.round(points / contract.tick);
}

// Dollar value of a one-point move, by futures product (used to read imports and to
// work out P&L from prices). Keep in step with tradeMath.js on the server.
export const POINT_VALUES = {
  ES: 50, MES: 5, NQ: 20, MNQ: 2, YM: 5, MYM: 0.5, RTY: 50, M2K: 5, EMD: 100,
  // CME E-nano contracts (launched August 2026): a tenth of the Micro E-minis' size.
  NES: 0.5, NNQ: 0.2, N2K: 0.5, NDOW: 0.05,
  CL: 1000, MCL: 100, QM: 500, NG: 10000, QG: 2500, RB: 42000, HO: 42000,
  GC: 100, MGC: 10, SI: 5000, SIL: 1000, HG: 25000, MHG: 2500, PL: 50, PA: 100,
  ZB: 1000, UB: 1000, ZN: 1000, TN: 1000, ZF: 1000, ZT: 2000,
  '6E': 125000, M6E: 12500, '6J': 12500000, '6B': 62500, M6B: 6250, '6A': 100000, M6A: 10000,
  '6C': 100000, '6S': 125000, '6N': 100000, '6M': 500000,
  ZC: 50, ZS: 50, ZW: 50, ZM: 100, ZL: 600, LE: 400, HE: 400, GF: 500,
  BTC: 5, MBT: 0.1, ETH: 50, MET: 0.1,
};

// US option symbols in the standard (OCC) format: root, expiry YYMMDD, C or P, strike × 1000,
// e.g. "SPY260330C00637000". One contract is 100 shares.
const OPTION_SYMBOL = /^[A-Z]{1,6}\d{6}[CP]\d{8}$/;
export const OPTION_MULTIPLIER = 100;
export function isOptionSymbol(symbol) {
  return OPTION_SYMBOL.test(String(symbol || '').toUpperCase().trim());
}

// Dollars per point for a futures symbol in any month ("MESZ6" -> 5), 100 for an option
// contract, or undefined (stocks, crypto and anything else: the plain price difference).
export function pointValue(symbol) {
  if (isOptionSymbol(symbol)) return OPTION_MULTIPLIER;
  return POINT_VALUES[rootSymbol(symbol)];
}
